import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { sysDeptUserDefaultListParams } from "../config";
import {
  addSysStaffToDept,
  createSysDept,
  createSysStaff,
  fetchSysDeptTree,
  fetchSysStaffList,
  fetchSysStaffSupervisors,
  moveSysStaffDept,
  resetSysStaffPassword,
  updateSysDept,
  updateSysStaff,
  updateSysStaffStatus,
  updateSysStaffSupervisors,
} from "../repo/sysDeptUserRepo";
import {
  flattenSysDeptOptions,
  flattenSysStaffOptions,
  normalizeSysStaffFilters,
} from "../service/sysDeptUserService";
import type {
  SysDeptMutation,
  SysStaffItem,
  SysStaffListFilters,
  SysStaffMutation,
  SysStaffPasswordResetMutation,
  SysStaffSupervisorItem,
  SysStaffSupervisorMutationItem,
} from "../types";

const sysDeptTreeQueryKey = ["sys_dept_user", "dept_tree"] as const;
const sysStaffListQueryKey = ["sys_dept_user", "staff_list"] as const;
const sysStaffCandidateQueryKey = ["sys_dept_user", "staff_candidates"] as const;

/** 管理组织和用户页面查询、分页和变更行为。 */
export function useSysDeptUserRuntime() {
  const queryClient = useQueryClient();
  const [selectedDeptId, setSelectedDeptId] = useState<number | undefined>(undefined);
  const [draftFilters, setDraftFilters] = useState<SysStaffListFilters>({});
  const [submittedFilters, setSubmittedFilters] = useState<SysStaffListFilters>({});
  const [page, setPage] = useState(sysDeptUserDefaultListParams.page);
  const [pageSize, setPageSize] = useState(sysDeptUserDefaultListParams.pageSize);
  const [includeDisabledDepts, setIncludeDisabledDepts] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");

  const deptTreeQuery = useQuery({
    queryKey: [...sysDeptTreeQueryKey, { includeDisabled: includeDisabledDepts }],
    queryFn: () => fetchSysDeptTree({ includeDisabled: includeDisabledDepts }),
  });

  const listParams = useMemo(
    () => ({
      ...sysDeptUserDefaultListParams,
      ...submittedFilters,
      deptId: selectedDeptId,
      page,
      pageSize,
    }),
    [page, pageSize, selectedDeptId, submittedFilters],
  );

  const staffListQuery = useQuery({
    queryKey: [...sysStaffListQueryKey, listParams],
    queryFn: () => fetchSysStaffList(listParams),
  });

  const staffCandidateQuery = useQuery({
    queryKey: sysStaffCandidateQueryKey,
    queryFn: () =>
      fetchSysStaffList({
        ...sysDeptUserDefaultListParams,
        isStaff: true,
        page: 1,
        pageSize: 100,
      }),
  });

  const supervisorsQuery = useMutation({
    mutationFn: fetchSysStaffSupervisors,
  });

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: sysDeptTreeQueryKey }),
      queryClient.invalidateQueries({ queryKey: sysStaffListQueryKey }),
      queryClient.invalidateQueries({ queryKey: sysStaffCandidateQueryKey }),
    ]);
  };

  const createDeptMutation = useMutation({
    mutationFn: createSysDept,
    onSuccess: invalidateAll,
  });
  const updateDeptMutation = useMutation({
    mutationFn: ({ deptId, payload }: { deptId: number; payload: SysDeptMutation }) =>
      updateSysDept(deptId, payload),
    onSuccess: invalidateAll,
  });
  const createStaffMutation = useMutation({
    mutationFn: createSysStaff,
    onSuccess: invalidateAll,
  });
  const updateStaffMutation = useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: SysStaffMutation }) =>
      updateSysStaff(userId, payload),
    onSuccess: invalidateAll,
  });
  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, status }: { userId: number; status: number }) =>
      updateSysStaffStatus(userId, { status }),
    onSuccess: invalidateAll,
  });
  const moveDeptMutation = useMutation({
    mutationFn: ({ userId, deptId }: { userId: number; deptId: number }) =>
      moveSysStaffDept(userId, { deptId }),
    onSuccess: invalidateAll,
  });
  const addStaffDeptMutation = useMutation({
    mutationFn: ({ userId, deptId }: { userId: number; deptId: number }) =>
      addSysStaffToDept(userId, { deptId }),
    onSuccess: invalidateAll,
  });
  const resetPasswordMutation = useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: number;
      payload: SysStaffPasswordResetMutation;
    }) => resetSysStaffPassword(userId, payload),
  });
  const updateSupervisorsMutation = useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: number;
      payload: SysStaffSupervisorMutationItem[];
    }) => updateSysStaffSupervisors(userId, payload),
    onSuccess: invalidateAll,
  });

  const deptTree = deptTreeQuery.data ?? [];
  const staffPage = staffListQuery.data ?? {
    items: [],
    total: 0,
    page,
    pageSize,
  };
  const candidateStaffItems = (staffCandidateQuery.data?.items ?? staffPage.items).filter(
    (item) => item.isStaff,
  );

  return {
    deptTree,
    deptOptions: flattenSysDeptOptions(deptTree),
    staffOptions: flattenSysStaffOptions(candidateStaffItems),
    selectedDeptId,
    includeDisabledDepts,
    draftFilters,
    items: staffPage.items,
    candidateStaffItems,
    loading: deptTreeQuery.isLoading || staffListQuery.isLoading || staffCandidateQuery.isLoading,
    page,
    pageSize,
    total: staffPage.total,
    errorMessage: deptTreeQuery.error || staffListQuery.error ? "组织或用户数据加载失败，请稍后重试。" : "",
    mutationErrorMessage,
    setDraftFilters,
    setIncludeDisabledDepts,
    setSelectedDeptId(deptId: number | undefined) {
      setSelectedDeptId(deptId);
      setPage(1);
    },
    clearMutationError() {
      setMutationErrorMessage("");
    },
    handleSearch() {
      setSubmittedFilters(normalizeSysStaffFilters(draftFilters));
      setPage(1);
    },
    handleShowAllStaff() {
      setSelectedDeptId(undefined);
      setPage(1);
    },
    handleReset() {
      setDraftFilters({});
      setSubmittedFilters({});
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    async handleCreateDept(payload: SysDeptMutation) {
      await runMutation(() => createDeptMutation.mutateAsync(payload), setMutationErrorMessage);
    },
    async handleUpdateDept(deptId: number, payload: SysDeptMutation) {
      await runMutation(
        () => updateDeptMutation.mutateAsync({ deptId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleCreateStaff(payload: SysStaffMutation) {
      await runMutation(() => createStaffMutation.mutateAsync(payload), setMutationErrorMessage);
    },
    async handleUpdateStaff(userId: number, payload: SysStaffMutation) {
      await runMutation(
        () => updateStaffMutation.mutateAsync({ userId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleToggleStaffStatus(item: SysStaffItem) {
      await runMutation(
        () => updateStatusMutation.mutateAsync({ userId: item.id, status: item.status === 1 ? 0 : 1 }),
        setMutationErrorMessage,
      );
    },
    async handleMoveStaffToSelectedDept(item: SysStaffItem) {
      if (!selectedDeptId || (item.deptIds.length === 1 && item.deptIds[0] === selectedDeptId)) {
        return;
      }
      await runMutation(
        () => moveDeptMutation.mutateAsync({ userId: item.id, deptId: selectedDeptId }),
        setMutationErrorMessage,
      );
    },
    async handleMoveStaffToDept(item: SysStaffItem, deptId: number) {
      if (!deptId || (item.deptIds.length === 1 && item.deptIds[0] === deptId)) {
        return;
      }
      await runMutation(
        () => moveDeptMutation.mutateAsync({ userId: item.id, deptId }),
        setMutationErrorMessage,
      );
    },
    async handleAddStaffToDept(items: SysStaffItem[], deptId: number) {
      if (!deptId || items.length === 0) {
        return;
      }
      await runMutation(
        () =>
          Promise.all(
            items.map((item) => addStaffDeptMutation.mutateAsync({ userId: item.id, deptId })),
          ),
        setMutationErrorMessage,
      );
    },
    async handleRemoveStaffFromDept(item: SysStaffItem, deptId: number) {
      if (!deptId || !item.deptIds.includes(deptId)) {
        return;
      }
      const nextDeptIds = item.deptIds.filter((id) => id !== deptId);
      await runMutation(
        () =>
          updateStaffMutation.mutateAsync({
            userId: item.id,
            payload: {
              username: item.username,
              realname: item.realname,
              nickname: item.nickname,
              phone: item.phone,
              email: item.email,
              avatar: item.avatar,
              sex: item.sex,
              mainDeptId: item.mainDeptId === deptId ? nextDeptIds[0] : item.mainDeptId,
              deptIds: nextDeptIds,
              status: item.status,
              userType: item.userType,
              supplierId: item.supplierId,
              wxOpenid: item.wxOpenid,
              wxUnionid: item.wxUnionid,
              isSubscribe: item.isSubscribe,
              isSuperuser: item.isSuperuser,
              isStaff: item.isStaff,
              supervisors: item.supervisors.map((supervisor) => ({
                supervisorId: supervisor.supervisorId,
                supervisorLevel: supervisor.supervisorLevel,
                orderNum: supervisor.orderNum,
              })),
            },
          }),
        setMutationErrorMessage,
      );
    },
    async handleLoadSupervisors(userId: number): Promise<SysStaffSupervisorItem[]> {
      const result = await supervisorsQuery.mutateAsync(userId);
      return result.items;
    },
    async handleSaveSupervisors(userId: number, payload: SysStaffSupervisorMutationItem[]) {
      await runMutation(
        () => updateSupervisorsMutation.mutateAsync({ userId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleResetStaffPassword(userId: number, payload: SysStaffPasswordResetMutation) {
      await runMutation(
        () => resetPasswordMutation.mutateAsync({ userId, payload }),
        setMutationErrorMessage,
      );
    },
  };
}

/** 统一执行变更并收口错误文案。 */
async function runMutation<T>(
  execute: () => Promise<T>,
  setMutationErrorMessage: (message: string) => void,
) {
  setMutationErrorMessage("");
  try {
    return await execute();
  } catch (error) {
    setMutationErrorMessage("保存失败，请检查填写内容后重试。");
    throw error;
  }
}
